/// <amd-module name="Dashboards/DashboardView/src/components/headers/DashboardSettingsManager" />
import * as React from "react";
import { connect } from "react-redux";

import { DashboardViewAwareState } from "../../model/DashboardViewState";
import { DashboardActions } from "../../redux/actions/Actions";
import * as Selectors from "../../redux/selectors/Selectors";

import { Dashboard } from "Dashboards/Common/Contracts/Dashboard";
import { ManageDashboardPanel } from "Dashboards/DashboardPanel/ManageDashboardPanel";

import { ITeamData, ITfsPageService } from "Tfs/Platform/Page";

import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";

/**
 * NOTE: This interface is copied from
 * Tfs\Service\WebAccess\Dashboards\Components\Shared\ManageDashboardDialog\ManageDashboardDialogShim.tsx
 * Any changes here must be updated there as well.
 */
interface ManageDashboardDialogShimProps {
    dashboard: Dashboard;
    team: { id: string; name: string };
    onSave: (updatedDashboard: Dashboard) => void;
    onDismiss: () => void;
}

interface ConnectedDashboardSettingsManagerOwnProps {}

interface ConnectedDashboardSettingsManagerMappedProps {
    dashboard: Dashboard;
    team: ITeamData;
    ownerIdentity: IIdentity;
    canManagePermissionsForDashboard: boolean;
}

interface DashboardSettingsManagerProps extends ConnectedDashboardSettingsManagerOwnProps, ConnectedDashboardSettingsManagerMappedProps {}

/**
 * Renders an experience for users to modify dashboard settings.
 * Resolves returned promise when the user completes modifying the dashboard settings.
 */
class DashboardSettingsManager extends VssComponent<DashboardSettingsManagerProps & typeof Actions> {
    private layoutManager: IVssLayoutManager;

    public constructor(props: DashboardSettingsManagerProps & typeof Actions, context?: IVssComponentContext) {
        super(props, context);
        this.layoutManager = context!.pageContext.getService("IVssLayoutManager");
    }

    public async componentDidMount(): Promise<void> {
        const dashboard: Dashboard | undefined = await this.renderDashboardSettingsManagerPrompt();
        if (dashboard != null) {
            this.props.onSettingsChanged(dashboard.name, dashboard.description, dashboard.refreshInterval, dashboard.ownerId);
        }

        this.props.onManagerClosed();
    }

    public render(): JSX.Element | null {
        return null;
    }

    private renderDashboardSettingsManagerPrompt(): Promise<Dashboard | undefined> {
        return new Promise(resolve => {
            if (isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards.new-dashboard-management-experience-feature", false)) {
                const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
                const pageData = tfsPageService.getData();
                const project = pageData!.project!;

                this.layoutManager.renderCallout((onDismiss: () => void) => {
                    const onDashboardUpdated = (updatedDashboard: Dashboard) => {
                        onDismiss();
                        resolve(updatedDashboard);
                    };
                    const onPanelDismiss = () => {
                        onDismiss();
                        resolve(undefined);
                    };

                    const { dashboard, team, ownerIdentity, canManagePermissionsForDashboard } = this.props;

                    return (
                        <ManageDashboardPanel
                            dashboard={dashboard}
                            onDashboardUpdated={onDashboardUpdated}
                            onDismiss={onPanelDismiss}
                            project={project}
                            team={team}
                            owner={ownerIdentity}
                            canManagePermissionsForDashboard={canManagePermissionsForDashboard}
                        />
                    );
                });
            } else {
                this.layoutManager.renderCallout(onDismiss => {
                    const onCompletedChangingDashboardSettings = (updatedDashboard?: Dashboard) => {
                        onDismiss();
                        resolve(updatedDashboard);
                    };

                    const shimProps: ManageDashboardDialogShimProps = {
                        dashboard: this.props.dashboard, // We assume the legacy ManageDashboardDialog doesn't modify the dashboard object we give it
                        team: this.props.team,
                        onSave: onCompletedChangingDashboardSettings,
                        onDismiss: onCompletedChangingDashboardSettings
                    };

                    return (
                        <LegacyComponent
                            modules={["Dashboards/Components/Shared/ManageDashboardDialog/ManageDashboardDialogShim"]}
                            wrappedType="ManageDashboardDialog"
                            {...shimProps}
                        />
                    );
                });
            }
        });
    }
}

const Actions = {
    onSettingsChanged: DashboardActions.updateDashboardSettings,
    onManagerClosed: DashboardActions.hideDashboardSettingsManager
};

function mapStateToProps(state: DashboardViewAwareState): ConnectedDashboardSettingsManagerMappedProps {
    return {
        team: Selectors.getDashboardTeamData(state)!,
        dashboard: Selectors.packServerDashboard(state),
        ownerIdentity: Selectors.getDashboardOwnerIdentity(state)!,
        canManagePermissionsForDashboard: Selectors.canManagePermissionsForDashboard(state)
    };
}

export const ConnectedDashboardSettingsManager = connect(
    mapStateToProps,
    Actions
)(DashboardSettingsManager);
